import { AnimationAction, AnimationClip, AnimationMixer, Object3D, Bone, Quaternion, Vector3 } from 'three';

/** One explicit clock: renderers decide when to advance, including motion LOD. */
export class AvatarAnimator {
  readonly mixer: AnimationMixer;
  readonly actions: Record<string, AnimationAction | undefined> = {};
  constructor(root: Object3D, clips: AnimationClip[]) {
    this.mixer = new AnimationMixer(root);
    for (const clip of clips) this.actions[clip.name] = this.mixer.clipAction(clip);
  }
  transition(from: string, to: string, idleSpeed = .65, duration = .2) {
    const next = this.actions[to], previous = this.actions[from];
    if (!next || previous === next) return false;
    const moving = (name: string) => name === 'walk' || name === 'run';
    const phase = previous && moving(from) && moving(to) && previous.getClip().duration > 0
      ? (previous.time / previous.getClip().duration) % 1 : 0;
    previous?.fadeOut(duration);
    next.reset();
    next.time = phase * next.getClip().duration;
    next.setEffectiveTimeScale(to === 'idle' ? idleSpeed : 1);
    next.fadeIn(duration).play();
    return true;
  }
  stop() { this.mixer.stopAllAction(); }
}

/** Reversible additive motion: restore the sampled pose before the next mixer tick. */
export class AvatarIdleMotion {
  private base = new Quaternion();
  private offset = new Quaternion();
  private axis = new Vector3(1, 0, 0);
  private applied = false;
  private time: number;
  private frequency: number;
  private weight = 0;
  constructor(private chest: Bone | undefined, seed: string) {
    let hash = 2166136261;
    for (const char of seed) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
    const fraction = (hash >>> 0) / 4294967296;
    this.time = fraction * 100;
    this.frequency = .18 + fraction * .1;
  }
  clear() {
    if (this.applied && this.chest) this.chest.quaternion.copy(this.base);
    this.applied = false;
  }
  apply(delta: number, enabled: boolean) {
    this.clear();
    this.time += Math.max(0, delta);
    this.weight += ((enabled ? 1 : 0) - this.weight) * (1 - Math.exp(-6 * Math.max(0, delta)));
    if (!this.chest || !enabled) return; // Never write over an emote or tracked pose.
    this.base.copy(this.chest.quaternion);
    const angle = Math.sin(this.time * this.frequency * Math.PI * 2) * .006 * this.weight;
    this.offset.setFromAxisAngle(this.axis, angle);
    this.chest.quaternion.multiply(this.offset);
    this.applied = true;
  }
}
